import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut, Menu, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import ConfirmDestructiveModal from '@/components/shared/ConfirmDestructiveModal';
import { useAuth } from '@/context/AuthContext';
import { useSidebar } from '@/context/SidebarContext';
import LanguageSelector from '@/components/shared/LanguageSelector';
import ThemeToggle from '@/components/shared/ThemeToggle';
import NotificationBell from '@/components/layout/NotificationBell';
import { Link } from 'react-router-dom';

function getInitials(first?: string, last?: string): string {
  const a = first?.[0] ?? '';
  const b = last?.[0] ?? '';
  return (a + b).toUpperCase() || '?';
}

export default function AppTopBar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { toggle: toggleMobile, collapsed } = useSidebar();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  return (
    <TooltipProvider delayDuration={200}>
      <header
        className="fixed top-0 right-0 z-30 h-16 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-[left] duration-300"
        style={{ left: `var(--app-sidebar-width, ${collapsed ? '72px' : '256px'})` }}
      >
        <div className="flex h-full items-center justify-between px-4 sm:px-6">
          {/* Left: mobile toggle */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon-sm"
              className="lg:hidden"
              onClick={toggleMobile}
              aria-label={t('nav.toggleMenu')}
            >
              <Menu className="size-[18px]" />
            </Button>
          </div>

          {/* Right: controls — lang first (distinct pill), then icons grouped, then user, then logout */}
          <div className="flex items-center gap-1.5">
            <LanguageSelector />
            {user && <NotificationBell />}
            <ThemeToggle />

            {user && (
              <>
                <Separator orientation="vertical" className="mx-1 h-6" />
                <Link
                  to="/profile"
                  className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={t('nav.profile', 'Perfil')}
                >
                  <Avatar className="size-7">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                      {getInitials(user.firstName, user.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-[13px] font-medium text-foreground">
                    {user.firstName} {user.lastName}
                  </span>
                  <ChevronDown className="size-3.5 text-muted-foreground hidden sm:inline" />
                </Link>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setShowLogoutModal(true)}
                      aria-label={t('nav.logout')}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <LogOut className="size-[18px]" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('nav.logout')}</TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        </div>
      </header>

      <ConfirmDestructiveModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={logout}
        title={t('nav.logoutConfirmTitle')}
        description={t('nav.logoutConfirmMessage')}
        confirmLabel={t('nav.logout')}
      />
    </TooltipProvider>
  );
}
